import { logger } from '@/lib/logger';

/**
 * Optimizes Clerk image URLs by adding size and quality parameters
 */
export const optimizeClerkImageUrl = (imageUrl: string, size: number = 96, quality: number = 85) => {
  if (!imageUrl || !imageUrl.includes('clerk')) return imageUrl;
  
  try {
    const url = new URL(imageUrl);
    if (url.hostname === 'img.clerk.com') {
      // Handle both proxy and direct URLs
      let baseUrl;
      if (url.search) {
        // For proxy URLs
        try {
          const params = JSON.parse(decodeURIComponent(url.search.slice(1)));
          baseUrl = params.src;
        } catch (e) {
          // If JSON parsing fails, use the URL as is
          baseUrl = imageUrl;
        }
      } else {
        // For direct URLs
        baseUrl = imageUrl;
      }

      // Add or update size and quality parameters
      const finalUrl = new URL(baseUrl);
      finalUrl.searchParams.set('width', size.toString());
      finalUrl.searchParams.set('height', size.toString());
      finalUrl.searchParams.set('quality', quality.toString());

      // If it was a proxy URL, re-encode it
      if (url.search) {
        const encodedParams = encodeURIComponent(JSON.stringify({
          type: "proxy",
          src: finalUrl.toString()
        }));
        return `https://img.clerk.com?${encodedParams}`;
      }

      return finalUrl.toString();
    }
    return imageUrl;
  } catch (e) {
    logger.error('Error optimizing Clerk image URL:', e);
    return imageUrl;
  }
};

/**
 * Generates a blurred data URL for image placeholders
 */
export const getBlurDataUrl = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url);
    const buffer = await response.arrayBuffer();
    const base64 = Buffer.from(buffer).toString('base64');
    return `data:image/jpeg;base64,${base64}`;
  } catch (error) {
    logger.error('Error generating blur data URL:', error);
    return '';
  }
};

/**
 * Validates image URLs and returns a default if invalid
 */
export const validateImageUrl = (url: string | undefined | null, defaultUrl: string = '/og.png'): string => {
  if (!url) return defaultUrl;
  try {
    new URL(url);
    return url;
  } catch {
    return defaultUrl;
  }
};

/**
 * Constants for image optimization
 */
export const IMAGE_CONSTANTS = {
  PROFILE: {
    THUMBNAIL: { width: 96, height: 96, quality: 85 },
    MEDIUM: { width: 256, height: 256, quality: 85 },
    LARGE: { width: 512, height: 512, quality: 90 }
  },
  OG: {
    DEFAULT: { 
      width: 800, 
      height: 420, 
      quality: 65 
    }
  }
} as const; 